import {
  THREAD_IMAGE_UPLOAD_TYPE,
  THREAD_UPLOAD_TYPES,
  type ThreadUploadType,
  type ThreadUploadTypeKnown,
} from '@teo/widget-shared';
import {
  getWidgetAuthHeaders,
  getWidgetSession,
  clearWidgetSession,
  updateWidgetSessionThreadId,
} from '../session/widget-session.js';

/** @deprecated Prefer `THREAD_UPLOAD_TYPES.IMAGE` from `@teo/widget-shared`. */
export { THREAD_IMAGE_UPLOAD_TYPE };

export type ThreadUploadResponse = {
  id: number;
  thread_id: number;
  type: string;
  path: string;
  fullPath: string;
  created_at: string;
  updated_at: string;
};

export type UploadFileRequest = {
  file: File;
  threadId: number | string;
  /** Form field `type`; default `THREAD_UPLOAD_TYPES.IMAGE`. Other values — when backend enum is ready. */
  uploadType?: ThreadUploadType;
};

export type UploadFileResult = {
  url: string;
  threadId: number;
};

export type UploadFileFn = (
  request: UploadFileRequest,
) => Promise<UploadFileResult>;

export class WidgetUploadError extends Error {
  readonly status: number;
  readonly code:
    | 'NO_THREAD'
    | 'UPLOAD_FAILED'
    | 'INVALID_RESPONSE'
    | 'TOO_LARGE'
    | 'UNSUPPORTED_FORMAT';

  constructor(
    code: WidgetUploadError['code'],
    message: string,
    status = 0,
  ) {
    super(message);
    this.name = 'WidgetUploadError';
    this.code = code;
    this.status = status;
  }
}

function readUploadErrorDetail(data: unknown): string | null {
  if (typeof data !== 'object' || data === null) return null;
  const record = data as Record<string, unknown>;
  for (const key of ['message', 'error', 'detail'] as const) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
}

export function buildThreadUploadUrl(
  apiBase: string,
  threadId: number | string,
): string {
  const base = apiBase.replace(/\/$/, '');
  return `${base}/api/thread/${threadId}/upload-file`;
}

function parseUploadResponse(data: unknown): UploadFileResult {
  if (typeof data !== 'object' || data === null) {
    throw new WidgetUploadError(
      'INVALID_RESPONSE',
      '[Teo Widget] Invalid upload response',
    );
  }

  const record = data as Partial<ThreadUploadResponse>;
  const url =
    typeof record.fullPath === 'string'
      ? record.fullPath
      : typeof (data as { url?: string }).url === 'string'
        ? (data as { url: string }).url
        : null;

  if (!url || typeof record.thread_id !== 'number') {
    throw new WidgetUploadError(
      'INVALID_RESPONSE',
      '[Teo Widget] Upload response missing fullPath',
    );
  }

  return { url, threadId: record.thread_id };
}

/**
 * POST /api/thread/{threadId}/upload-file
 * Body: multipart `file`, `type` (see `THREAD_UPLOAD_TYPES` in `@teo/widget-shared`).
 */
/** Abort hung mobile uploads instead of spinning forever. */
export const THREAD_UPLOAD_TIMEOUT_MS = 60_000;

/** Prefer AbortSignal.timeout; fall back for older WebKit. */
export function createUploadTimeoutSignal(
  ms: number = THREAD_UPLOAD_TIMEOUT_MS,
): AbortSignal {
  if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
    return AbortSignal.timeout(ms);
  }
  const controller = new AbortController();
  setTimeout(() => {
    controller.abort(
      typeof DOMException !== 'undefined'
        ? new DOMException('Upload timed out', 'TimeoutError')
        : undefined,
    );
  }, ms);
  return controller.signal;
}

export function createHttpUploadFile(apiBase: string): UploadFileFn {
  return async ({
    file,
    threadId,
    uploadType = THREAD_UPLOAD_TYPES.IMAGE,
  }) => {
    const session = getWidgetSession();
    if (!session) {
      throw new Error('[Teo Widget] Session required to upload file');
    }

    if (threadId == null || String(threadId).trim() === '') {
      throw new WidgetUploadError(
        'NO_THREAD',
        '[Teo Widget] thread_id is required before upload',
      );
    }

    if (file.size <= 0) {
      throw new WidgetUploadError(
        'UPLOAD_FAILED',
        '[Teo Widget] Image file is empty',
        0,
      );
    }

    const form = new FormData();
    const fileName = file.name?.trim() || 'image.jpg';
    form.append('file', file, fileName);
    form.append('type', uploadType);

    let response: Response;
    try {
      response = await fetch(buildThreadUploadUrl(apiBase, threadId), {
        method: 'POST',
        credentials: 'omit',
        headers: {
          Accept: 'application/json',
          ...getWidgetAuthHeaders(),
        },
        body: form,
        signal: createUploadTimeoutSignal(),
      });
    } catch (error) {
      if (
        error instanceof Error &&
        (error.name === 'TimeoutError' || error.name === 'AbortError')
      ) {
        throw new WidgetUploadError(
          'UPLOAD_FAILED',
          '[Teo Widget] Upload timed out',
          0,
        );
      }
      throw error;
    }

    if (!response.ok) {
      if (response.status === 401) {
        clearWidgetSession(session.slug);
      }
      let detail: string | null = null;
      try {
        detail = readUploadErrorDetail(await response.json());
      } catch {
        detail = null;
      }
      throw new WidgetUploadError(
        'UPLOAD_FAILED',
        detail
          ? `[Teo Widget] Upload failed (${response.status}): ${detail}`
          : `[Teo Widget] Upload failed (${response.status})`,
        response.status,
      );
    }

    const data: unknown = await response.json();
    const result = parseUploadResponse(data);

    if (!session.threadId) {
      updateWidgetSessionThreadId(result.threadId);
    }

    return result;
  };
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** Dev / Storybook upload transport. */
export function createMockUploadFile(options?: {
  latencyMs?: number;
  threadId?: number;
}): UploadFileFn {
  const latencyMs = options?.latencyMs ?? 700;
  const defaultThreadId = options?.threadId ?? 5753;

  return async ({ file, threadId }) => {
    await delay(latencyMs);

    const resolvedThreadId = Number(threadId) || defaultThreadId;
    const objectUrl = URL.createObjectURL(file);

    return {
      url: objectUrl,
      threadId: resolvedThreadId,
    };
  };
}
